import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { motion } from 'framer-motion';
import OchaIcon from '../components/OchaIcon';

const facilities = [
    {
        icon: "Shelter",
        title: "Residential Accommodation",
        description: "Well-furnished residential quarters with comfortable bedding, clean linen, and attached washrooms for up to 50 participants. Separate blocks are available for men and women, ensuring privacy and convenience during multi-day training programs."
    },
    {
        icon: "Food",
        title: "Kitchen & Dining Hall",
        description: "A spacious, hygienic kitchen and dining facility serving nutritious vegetarian meals — breakfast, lunch, evening tea with snacks, and dinner. The menu is thoughtfully planned to cater to diverse dietary preferences of participants from across Maharashtra."
    },
    {
        icon: "Community-building",
        title: "Conference Hall (500 Capacity)",
        description: "A state-of-the-art air-conditioned conference hall equipped with projector, sound system, and stage — capable of hosting up to 500 attendees for seminars, workshops, state-level conventions, and large-scale training events."
    },
    {
        icon: "Education",
        title: "Training Halls & Boardroom",
        description: "Two dedicated training halls with modern AV equipment, whiteboards, and flexible seating arrangements for interactive workshops. A separate boardroom is available for leadership meetings, strategy sessions, and small-group discussions."
    },
    {
        icon: "Community-engagement",
        title: "Open Learning & Exhibition Spaces",
        description: "Landscaped open-air learning areas ideal for group activities, field demonstrations, exhibitions, and outdoor training sessions. These spaces foster experiential learning and hands-on engagement with agricultural and environmental themes."
    },
    {
        icon: "Map",
        title: "Strategic Location & Accessibility",
        description: "Situated on the Pune–Kolhapur highway corridor, just 18 km from Karad and 60 km from Sangli. Well-connected by road and rail, the center is easily reachable from Pune (approx. 3 hours), Mumbai, Kolhapur, and other major cities in Maharashtra."
    }
];

const cardVariants = {
    offscreen: { opacity: 0, scale: 0.9 },
    onscreen: {
        opacity: 1,
        scale: 1,
        transition: { type: "spring", bounce: 0.3, duration: 0.8 }
    }
};

const TrainingCenter = ({ darkMode }) => {
    const cardStyle = {
        backgroundColor: darkMode ? '#343a40' : '#ffffff',
        color: darkMode ? '#f8f9fa' : '#212529',
        border: darkMode ? '1px solid #495057' : '1px solid #dee2e6'
    };

    return (
        <Container style={{ minHeight: '100vh', paddingTop: '30px', paddingBottom: '30px' }}>
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <h2 className="text-center mb-3" style={{ color: darkMode ? '#A5D6A7' : '#2E7D32' }}>
                    PSGS Training Center
                </h2>
                <p className="text-center mb-4" style={{ maxWidth: '900px', margin: '0 auto', fontSize: '1.1rem' }}>
                    PSGS proudly operates a fully equipped residential Training Center at Kadepur, Kadegaon,
                    offering a joyful and enriching learning environment for participants across Maharashtra.
                    The center combines accessibility, comfort, and modern infrastructure, making it an ideal venue
                    for trainings, conferences, capacity-building workshops, and knowledge-sharing events managed
                    by PSGS's dedicated team.
                </p>
                <p className="text-center mb-5" style={{ maxWidth: '850px', margin: '0 auto', fontSize: '0.98rem', color: '#555' }}>
                    Over the years, the center has hosted government officials, CSR partners, grassroots workers,
                    SHG members, farmers, and community leaders — serving as a hub for collaborative learning
                    and rural innovation. With residential facilities, modern AV-equipped halls, and open-air
                    demonstration areas, the center is designed to support immersive, multi-day programs.
                </p>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mb-4"
            >
                <Card style={cardStyle} className="shadow overflow-hidden">
                    <Card.Img
                        variant="top"
                        src="/traning_center.png"
                        alt="PSGS Training Center"
                        style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                    />
                </Card>
            </motion.div>

            <Row className="mb-5">
                {facilities.map((facility, index) => (
                    <Col md={4} sm={6} key={index} className="mb-4">
                        <motion.div
                            initial="offscreen"
                            whileInView="onscreen"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={cardVariants}
                            style={{ height: '100%' }}
                        >
                            <Card style={cardStyle} className="shadow-sm h-100 text-center p-3">
                                <Card.Body className="d-flex flex-column align-items-center">
                                    <div
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            backgroundColor: darkMode ? '#2E7D32' : '#2E7D32',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginBottom: '15px'
                                        }}
                                    >
                                        <OchaIcon name={facility.icon} size={40} color="white" />
                                    </div>
                                    <Card.Title style={{ color: darkMode ? '#A5D6A7' : '#2E7D32', fontSize: '1.2rem' }}>
                                        {facility.title}
                                    </Card.Title>
                                    <Card.Text>{facility.description}</Card.Text>
                                </Card.Body>
                            </Card>
                        </motion.div>
                    </Col>
                ))}
            </Row>

            <Row>
                <Col md={12}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Card style={{ ...cardStyle, borderLeft: '4px solid #2E7D32' }} className="shadow">
                            <Card.Body>
                                <h4 style={{ color: darkMode ? '#A5D6A7' : '#2E7D32' }}>
                                    Location & How to Reach
                                </h4>
                                <p>
                                    The PSGS Training Center is strategically located near the Pune–Kolhapur national highway
                                    in the Kadegaon taluka of Sangli district. It is just 18 km from Karad railway station
                                    and 60 km from Sangli city, making it conveniently accessible by both road and rail
                                    from Pune, Mumbai, Kolhapur, and Satara.
                                </p>
                                <p>
                                    Participants traveling from Pune can reach in approximately 3 hours via NH48.
                                    Local transport is available from Karad and Kadegaon bus stands. The center provides
                                    pick-up coordination for group arrivals on request.
                                </p>
                                <p className="mb-0">
                                    <strong>Address:</strong> 1418, Pusedavali Road, Kadepur, Kadegaon, Sangli, Maharashtra 415305
                                </p>
                            </Card.Body>
                        </Card>
                    </motion.div>
                </Col>
            </Row>
        </Container>
    );
};

export default TrainingCenter;
